"use client";

import Translations from "@translate/components/EmailLogin/steps/EmailInputStepMobile";
import React from "react";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import type { EmailInputStepProps } from "../types";
import { SIGN_IN_PAGE_COPY } from "../signInPageCopy";
import { LoadingButton } from "../LoadingButton";
import {
  EmailInputField,
  useEmailInputCanSubmit,
} from "../shared/EmailInputField";
import { SignInOrContinueLabel } from "../shared/SignInOrContinueLabel";
import { SocialLoginButtons } from "../shared/SocialLoginButtons";
import { EmailLoginTermsCheckbox } from "../shared/EmailLoginTermsCheckbox";
import { useInviteCodeEntry } from "@components/InviteCode/useInviteCodeEntry";

const I18n = I18nUtil.createScoped(Translations);

export function EmailInputStepMobile({
  email,
  onEmailChange,
  onContinue,
  sending = false,
  emailFormatError = false,
  agreed,
  onAgreedChange,
  termsLink,
  privacyLink,
  onSocialClick,
  socialLoading = false,
  className,
  hideTitle = false,
  useV301SignInChrome = false,
  showInviteCodeEntry = false,
}: EmailInputStepProps) {
  const inviteCode = useInviteCodeEntry({
    page: "sign_in",
    tone: "on_light",
    className: "mt-8",
  });
  const canSubmit = useEmailInputCanSubmit(email, agreed, sending);
  const isV301Flat = hideTitle && useV301SignInChrome;
  const continueLabel = isV301Flat
    ? SIGN_IN_PAGE_COPY.continueWithEmail
    : I18n.t("Continue with email");

  return (
    <div
      className={cn(
        "flex w-full flex-col",
        isV301Flat ? "gap-8" : "gap-6",
        className,
      )}
      data-testid="email-input-step-mobile"
    >
      {!hideTitle && (
        <h2
          aria-label="Email Input Step h2"
          data-openreplay-label="login_emailinputstep_h2"
          id="sem-email-input-step-h2-2"
          className="headline_regular_m shrink-0 text-content_primary"
        >
          {I18n.t("Sign up or log in")}
        </h2>
      )}

      <div
        className={cn("flex w-full flex-col", isV301Flat ? "gap-8" : "gap-4")}
      >
        <div
          className="flex w-full flex-col gap-4"
          data-testid="sign-in-email-cta-group"
        >
          <EmailInputField
            variant="mobile"
            email={email}
            onEmailChange={onEmailChange}
            emailFormatError={emailFormatError}
            canSubmit={canSubmit}
            placeholder={
              isV301Flat ? SIGN_IN_PAGE_COPY.emailPlaceholder : undefined
            }
          />

          <LoadingButton
            data-openreplay-label="login_emailloginemailinputstep_click"
            type="button"
            disabled={!canSubmit}
            loading={sending}
            onTouchStart={(e) => e.preventDefault()}
            onTouchEnd={onContinue}
            className={cn(
              "button md_button primary_button button_l flex h-12 w-full items-center justify-center text-content_inverse_primary",
              isV301Flat ? "rounded-lg" : "rounded-2",
            )}
          >
            {continueLabel}
          </LoadingButton>
        </div>

        {onSocialClick && (
          <div className="flex w-full flex-col gap-4">
            {isV301Flat && <SignInOrContinueLabel />}
            <SocialLoginButtons
              variant="mobile"
              onSocialClick={onSocialClick}
              socialLoading={socialLoading}
              useV301Chrome={isV301Flat}
            />
          </div>
        )}

        <EmailLoginTermsCheckbox
          variant="mobile"
          agreed={agreed}
          onAgreedChange={onAgreedChange}
          termsLink={termsLink}
          privacyLink={privacyLink}
          className={isV301Flat ? "pt-1" : "pt-1"}
        />

        {/* 邀请码入口两态（Figma 110013:130019 / 130291，与 Terms 底边相距 32px），
            点击打开填码底部弹层，同页覆盖、已填邮箱不丢（fe-spec §3.3.4）。 */}
        {showInviteCodeEntry && (
          <>
            {inviteCode.entryRow}
            {inviteCode.sheet}
          </>
        )}
      </div>
    </div>
  );
}
